import React, { useState, useRef, useCallback, useEffect, useContext } from 'react';
import { useDropzone } from 'react-dropzone';
import { Activity, RefreshCw, AlertCircle, Camera, FileText, Sliders, Disc, Hammer, Cloud, MapPin, Settings } from 'lucide-react';
import axios from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import { Card, Button, ButtonGroup, ToggleButton } from '../components/ui';
import { Button as DSButton } from '../components/ds/Button';
import { Card as DSCard } from '../components/ds/Card';
import { Spinner } from '../components/ds/Spinner';
import { SliderInput } from '../components/fitter/SliderInput';
import { AdvSliderInput } from '../components/fitter/AdvSliderInput';
import { DispersionChart } from '../components/fitter/DispersionChart';
import { FitterMatrix } from '../components/fitter/FitterMatrix';
import CurrentGearInput from '../components/fitter/CurrentGearInput';
import OptimizationSuggestionCard from '../components/fitter/OptimizationSuggestionCard';
import { ErrorBoundary } from '../components/ErrorBoundary';
import CSVUploader from '../components/fitter/CSVUploader';
import OCRUploader from '../components/fitter/OCRUploader';

const TOOLTIPS = {
  club_speed: "Speed of the club head (mph).",
  ball_speed: "Speed of the ball (mph). Efficiency check.",
  spin_rate: "Total backspin in RPM. Critical for distance and trajectory. Lower spin = more roll, higher spin = more stopping power.",
  launch_angle: "Vertical launch angle (deg).",
  attack_angle: "Hitting up (+) or down (-).",
  spin_axis: "Ball curvature direction in degrees. + is Fade/Slice, - is Draw/Hook. NOT spin rate.",
  side_spin: "Side spin in RPM. + is Fade/Slice (right curve), - is Draw/Hook (left curve). Alternative to Spin Axis.",
  handicap: "Used for forgiveness calculation.",
  wrist_to_floor: "Used for shaft length static fit."
};

// Helper to serialize error objects from backend validation
const serializeError = (error) => {
  if (typeof error === 'string') return error;
  if (Array.isArray(error)) {
    // Pydantic validation errors are arrays of {type, loc, msg, input, ctx, url}
    return error.map(e => e.msg || JSON.stringify(e)).join(', ');
  }
  if (typeof error === 'object' && error !== null) {
    return error.msg || error.message || JSON.stringify(error);
  }
  return String(error);
};

export default function FitterNoAnalysis({ prefillData, onPrefillUsed }) {
  const [mode, setMode] = useState('manual');
  const [fitType, setFitType] = useState('Driver');
  const [dexterity, setDexterity] = useState('RH');  // RH or LH
  const [spinInputMode, setSpinInputMode] = useState('axis'); // 'axis' (degrees) or 'sidespin' (RPM)
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [clubType, setClubType] = useState('Driver');
  const [ironType, setIronType] = useState('7i');
  const fileInputRef = useRef(null);
  const { user, login } = useContext(AuthContext);
  const prefillApplied = useRef(false);
  const [usePlayingConditions, setUsePlayingConditions] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showDispersion, setShowDispersion] = useState(false);
  const [showTrajectory, setShowTrajectory] = useState(false);

  // AI Fitter flow state - Simplified to 2 pages
  const [currentGear, setCurrentGear] = useState(null);
  const [optimizationData, setOptimizationData] = useState(null);
  const [showGearInput, setShowGearInput] = useState(false);
  const gearInputRef = useRef(null);

  // Upload confirmation state
  const [uploadedData, setUploadedData] = useState(null); // Stores upload result before showing matrix
  const [uploadStatus, setUploadStatus] = useState(null); // "CSV uploaded" or "Image uploaded"

  // NO SHOT CURATION - Testing without analysis step

  // Request cancellation support
  const [uploadController, setUploadController] = useState(null);

  // Upload progress tracking
  const [uploadProgress, setUploadProgress] = useState(0);

  const [manualData, setManualData] = useState({
    club_speed: 105, ball_speed: 136.5, spin_rate: 2400, launch_angle: 12,
    spin_axis: null, side_spin: null, handicap: null, wrist_to_floor: null,
    altitude_ft: null, temp_f: null, humidity_pct: null, wind_speed_mph: null, wind_direction_deg: null,
    // Additional Metrics - ALL null by default (hidden/OFF)
    attack_angle: null, club_path: null, face_angle: null, face_to_path: null, dynamic_loft: null,
    max_height: null, descent_angle: null, flight_time: null, total_distance: null, offline_distance: null,
    spin_loft: null, smash_factor: null, low_point: null,
    // Unused advanced fields (kept for backward compatibility)
    launch_direction: null, roll_distance: null, back_spin: null, closure_rate: null,
    curve: null, impact_x: null, impact_y: null, swing_plane: null,
  });
  const [ballSpeedOverride, setBallSpeedOverride] = useState(false);
  const smashFactor = 1.30;
  const [weather, setWeather] = useState(null);

  // Reset gear and optimization when changing modes at input stage
  useEffect(() => {
    if (!result) {
      setCurrentGear(null);
      setOptimizationData(null);
      // Clear file input when switching modes
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [mode, fitType, result]);

  // Get weather on load
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        try {
          const res = await axios.get(`/weather/current?lat=${latitude}&lon=${longitude}`);
          setWeather(res.data);
          // Set manual data to current conditions, but let user override
          setManualData(prev => ({
            ...prev,
            altitude_ft: res.data.altitude_ft,
            temp_f: res.data.temp_f,
            humidity_pct: res.data.humidity_pct,
            wind_speed_mph: res.data.wind_speed_mph,
            wind_direction_deg: res.data.wind_direction_deg,
          }));
        } catch (error) {
          console.error("Failed to fetch weather data", error);
        }
      }, () => {
        console.error("Geolocation permission denied.");
      });
    }
  }, []);

  // Handle prefill data from History "Open in Fitter"
  useEffect(() => {
    if (prefillData && !prefillApplied.current) {
      prefillApplied.current = true;
      // Set club type
      const club = prefillData.club_type || 'Driver';
      if (['Driver', 'Fairway', 'Hybrid'].includes(club)) {
        setFitType('Driver');
        setClubType(club);
      } else {
        setFitType('Iron');
        setIronType(club);
      }
      // Set manual data
      setManualData(prev => ({
        ...prev,
        club_speed: prefillData.club_speed || prev.club_speed,
        ball_speed: prefillData.ball_speed || prev.ball_speed,
        spin_rate: prefillData.spin_rate || prev.spin_rate,
        launch_angle: prefillData.launch_angle || prev.launch_angle,
        attack_angle: prefillData.attack_angle || prev.attack_angle,
        spin_axis: prefillData.spin_axis || prev.spin_axis,
      }));
      setBallSpeedOverride(true);
      setMode('manual');
      // Clear prefill after applying
      if (onPrefillUsed) onPrefillUsed();
      // Auto-run analysis after a short delay
      setTimeout(() => {
        document.getElementById('run-analysis-btn')?.click();
      }, 100);
    }
  }, [prefillData, onPrefillUsed]);

  // Cleanup: Cancel pending uploads on unmount
  useEffect(() => {
    return () => {
      if (uploadController) {
        uploadController.abort();
      }
    };
  }, [uploadController]);

  // Update ball speed when club speed changes (if not overridden)
  const handleClubSpeedChange = (newClubSpeed) => {
    if (!ballSpeedOverride) {
      setManualData({ ...manualData, club_speed: newClubSpeed, ball_speed: Math.round(newClubSpeed * smashFactor * 10) / 10 });
    } else {
      setManualData({ ...manualData, club_speed: newClubSpeed });
    }
  };

  const handleManual = async () => {
    if (loading) return; // Prevent concurrent requests
    setLoading(true); setError(null);
    try {
      // Get gear data from ref if toggle is on
      const gear = showGearInput && gearInputRef.current ? gearInputRef.current.getGearData() : null;
      setCurrentGear(gear);

      const payload = {
        // Category must match database: Driver, Fairway, Hybrid, Iron, Wedge
        category: fitType === 'Driver' ? clubType : 'Iron',
        club_type: fitType === 'Driver' ? clubType : 'Iron',
        current_club: fitType === 'Driver' ? 'Driver' : ironType,
        dexterity: dexterity,
        ...manualData,
        ball_id: gear?.ballId || null  // Get ball from gear, or null if no gear
      };
      const res = await axios.post(`/analyze/manual?mode=matrix&playing_conditions=${usePlayingConditions}`, payload);
      if (res.data.status === 'error') throw new Error(res.data.message);

      // If gear is provided, also call optimization endpoint
      let optimizationResult = null;
      if (gear) {
        try {
          const shot_data = {
            ball_speed: manualData.ball_speed,
            launch_angle: manualData.launch_angle,
            spin_rate: manualData.spin_rate
          };
          const club_specs = {
            current_loft: gear.loftDegrees,
            adjustability: gear.adjustability || { loft_range: [-1.5, 0, 1.5] }
          };
          const optimizeRes = await axios.post('/optimize', { shot_data, club_specs });
          optimizationResult = optimizeRes.data;
        } catch (err) {
          console.error('Optimization failed:', err);
          // Continue without optimization if it fails
        }
      }

      setOptimizationData(optimizationResult);
      setResult({ ...res.data, input: payload });
    } catch (e) {
      setError(serializeError(e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };


  const saveSession = async (matrixHead = null, matrixShaft = null, matrixStats = null) => {
    // For legacy mode, check if we have strategies
    if (!matrixHead && !result?.strategies?.distance) return;
    if (!user) {
      alert("Please sign in to save your session!");
      login();
      return;
    }
    setSaving(true);
    try {
      // Use aggregated data from CSV upload if available, otherwise manual data
      const aggregated = result.aggregated || manualData;
      // Use selected club from result (for CSV/OCR) or from state (for manual)
      const clubToSave = result.selectedClub || (fitType === 'Driver' ? clubType : ironType);

      let headName, shaftName, carryToSave;

      if (matrixHead && matrixShaft && matrixStats) {
        // Matrix mode: use the selected head/shaft/stats from the matrix
        headName = matrixHead.name;
        shaftName = matrixShaft.name;
        carryToSave = matrixStats.carry;
      } else {
        // Legacy mode: use distance strategy
        const actualCarry = aggregated.carry_distance || result.best_shot?.carry_distance;
        const theoreticalCarry = result.strategies.distance.stats.predicted_carry;
        carryToSave = actualCarry || theoreticalCarry;
        headName = result.strategies.distance.head.model;
        shaftName = result.strategies.distance.shaft.model;
      }

      // Build session data - prioritize aggregated (CSV/OCR) over manual input
      const sessionData = {
        club_type: clubToSave,
        club_speed: aggregated.club_speed || manualData.club_speed,
        ball_speed: aggregated.ball_speed || manualData.ball_speed,
        spin_rate: aggregated.spin_rate || manualData.spin_rate,
        launch_angle: aggregated.launch_angle || manualData.launch_angle,
        carry_distance: Math.round(carryToSave),
        recommended_head: headName,
        recommended_shaft: shaftName,
        shots: result.shots || []  // Include individual shots from CSV/OCR
      };

      // Only include additional manual metrics if we're in manual mode (no aggregated data)
      if (!result.aggregated) {
        // Add advanced metrics only for manual mode
        sessionData.attack_angle = manualData.attack_angle;
        sessionData.spin_axis = manualData.spin_axis;
        sessionData.side_spin = manualData.side_spin;
        sessionData.handicap = manualData.handicap;
        sessionData.wrist_to_floor = manualData.wrist_to_floor;
        sessionData.altitude_ft = manualData.altitude_ft;
        sessionData.temp_f = manualData.temp_f;
        sessionData.humidity_pct = manualData.humidity_pct;
        sessionData.wind_speed_mph = manualData.wind_speed_mph;
        sessionData.wind_direction_deg = manualData.wind_direction_deg;
      }

      await axios.post(`/history`, sessionData);
      alert(`Session Saved! ${result.shots?.length ? `(${result.shots.length} shots recorded)` : ''}`);
    } catch (e) { alert("Save failed: " + e.message); } finally { setSaving(false); }
  };

  const handleUpload = async (file, endpoint) => {
    if (loading) return; // Prevent concurrent requests

    // Cancel previous request if exists
    if (uploadController) {
      uploadController.abort();
    }

    const controller = new AbortController();
    setUploadController(controller);

    setLoading(true);
    setError(null);
    setUploadStatus(null);

    try {
      // Validate file size (10MB for images, 5MB for CSV)
      const isOCR = endpoint.includes('ocr');
      const maxSize = isOCR ? 10 * 1024 * 1024 : 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(`File too large. Maximum size is ${maxSize / 1024 / 1024}MB.`);
      }

      // Validate file type
      if (isOCR && !file.type.startsWith('image/')) {
        throw new Error('Please upload an image file (JPG, PNG, etc.)');
      }
      if (!isOCR && !file.name.toLowerCase().endsWith('.csv')) {
        throw new Error('Please upload a CSV file');
      }

      const formData = new FormData();
      formData.append('file', file);

      // Pass club type and dexterity as query params for fitting context
      const club = fitType === 'Driver' ? clubType : ironType;
      // Category must match database: Driver, Fairway, Hybrid, Iron, Wedge
      const category = fitType === 'Driver' ? clubType : 'Iron';

      const res = await axios.post(
        `/${endpoint}?mode=matrix&category=${category}&club_type=${club}&dexterity=${dexterity}&playing_conditions=${usePlayingConditions}`,
        formData,
        {
          signal: controller.signal,
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percentCompleted);
          }
        }
      );

      if (res.data.status === 'error') throw new Error(res.data.message);

      // Store uploaded data but don't show results yet - wait for user to skip or optimize
      const uploadData = { ...res.data, selectedClub: club, selectedCategory: category };
      setUploadedData(uploadData);
      setUploadStatus(isOCR ? 'Image uploaded successfully' : 'CSV uploaded successfully');

      // NO SHOT CURATION - Skip analysis step

      setUploadProgress(0); // Reset progress on success
    } catch (e) {
      // Enhanced error messages
      const errorMsg = e.response?.data?.detail || e.message;
      setError(serializeError(errorMsg));
    } finally {
      setLoading(false);
      setUploadProgress(0); // Reset progress
    }
  };

  /**
   * Process uploaded data and show fitting results.
   * Called when user clicks "Calculate Fit" after upload.
   * Handles both curated and non-curated data paths.
   *
   * @param {Object|null} gear - Optional current gear for optimization
   */
  const proceedWithUploadedData = async (gear = null) => {
    // Only proceed if we have uploaded data (CSV/OCR mode)
    if (!uploadedData || loading) return; // Prevent concurrent requests

    setLoading(true);
    setError(null);

    try {
      let optimizationResult = null;

      // NO CURATION - use raw aggregated data directly
      const dataToUse = uploadedData.aggregated || {};

      // If user provided current gear, run optimization analysis
      if (gear) {
        try {
          const shot_data = {
            ball_speed: dataToUse.ball_speed,
            launch_angle: dataToUse.launch_angle,
            spin_rate: dataToUse.spin_rate
          };
          const club_specs = {
            current_loft: gear.loftDegrees,
            adjustability: gear.adjustability || { loft_range: [-1.5, 0, 1.5] }
          };
          const optimizeRes = await axios.post('/optimize', { shot_data, club_specs });
          optimizationResult = optimizeRes.data;
        } catch (err) {
          console.error('Optimization failed:', err);
          // Don't fail entire flow if optimization fails - it's optional
        }
      }

      setOptimizationData(optimizationResult);

      // Display results and clean up upload state (NO CURATION)
      setResult(uploadedData);
      setUploadedData(null);
      setUploadStatus(null);

    } catch (e) {
      setError(serializeError(e.response?.data?.detail || e.message));
    } finally {
      // CRITICAL: Always stop loading spinner, even on error
      setLoading(false);
    }
  };

  // NO SHOT CURATION HANDLERS NEEDED

  const onDrop = useCallback((files) => handleUpload(files[0], 'analyze/upload'), [fitType, clubType, ironType, dexterity]);
  const { getRootProps, getInputProps } = useDropzone({ onDrop, accept: { 'text/csv': ['.csv'] }, maxFiles: 1 });

  return (
    <ErrorBoundary>
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Hero Section */}
      <div className="text-center py-8 px-4">
        <h1 className="font-heading font-extrabold text-3xl md:text-4xl text-text-primary mb-2">
          Find Your Perfect Fit
        </h1>
        <p className="text-text-secondary text-base max-w-2xl mx-auto">
          Leverage Shot Data from your sim session or your launch monitor to dial in your perfect build.
        </p>
      </div>

      {!result ? (
        <>
          <div className="flex flex-col items-center gap-6">
            <div className="flex flex-wrap items-center justify-center gap-3">
              <div className="flex bg-surface p-1 rounded-full border border-border">
                <button onClick={() => setFitType('Driver')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${fitType === 'Driver' ? 'bg-primary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>Wood Fit</button>
                <button onClick={() => setFitType('Iron')} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${fitType === 'Iron' ? 'bg-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>Iron Fit</button>
              </div>
              <div className="flex bg-surface p-1 rounded-lg border border-border">
                <button onClick={() => setDexterity('LH')} className={`px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${dexterity === 'LH' ? 'bg-surface-light text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>LH</button>
                <button onClick={() => setDexterity('RH')} className={`px-2 py-1.5 rounded-md text-sm font-medium transition-colors ${dexterity === 'RH' ? 'bg-surface-light text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>RH</button>
              </div>
              <div className="flex bg-surface/50 p-1 rounded-lg border border-border">
                <button onClick={() => setMode('manual')} className={`px-3 py-1.5 rounded-md flex gap-1.5 text-sm transition-colors ${mode === 'manual' ? 'bg-primary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}><Sliders size={14} /> Manual</button>
                <button onClick={() => setMode('csv')} className={`px-3 py-1.5 rounded-md flex gap-1.5 text-sm transition-colors ${mode === 'csv' ? 'bg-primary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}><FileText size={14} /> CSV</button>
                <button onClick={() => setMode('ocr')} className={`px-3 py-1.5 rounded-md flex gap-1.5 text-sm transition-colors ${mode === 'ocr' ? 'bg-primary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}><Camera size={14} /> Photo AI</button>
              </div>
            </div>
          </div>

          {mode === 'manual' ? (
            <Card padding="p-6" className="space-y-6 animate-in fade-in">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold flex items-center gap-2">{fitType === 'Driver' ? <Disc className="text-primary" /> : <Hammer className="text-success" />} Inputs</h2>
                {fitType === 'Driver' ? (
                  <div className="flex bg-background rounded-lg p-1 border border-border">{['Driver', 'Fairway', 'Hybrid'].map(t => <button key={t} onClick={() => setClubType(t)} className={`px-3 py-1 text-xs font-medium rounded transition-colors ${clubType === t ? 'bg-primary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>{t}</button>)}</div>
                ) : (
                  <div className="flex bg-background rounded-lg p-1 border border-border gap-0.5">{['4i', '5i', '6i', '7i', '8i', '9i', 'PW'].map(t => <button key={t} onClick={() => setIronType(t)} className={`px-2 py-1 text-xs font-medium rounded transition-colors ${ironType === t ? 'bg-secondary text-text-primary' : 'text-text-secondary hover:text-text-primary'}`}>{t}</button>)}</div>
                )}
              </div>

              {/* Required Inputs Section */}
              <div className="space-y-4">
                <div className="text-xs font-bold text-secondary uppercase tracking-wider">Required for Calculation</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <SliderInput label="Club Speed" val={manualData.club_speed} set={handleClubSpeedChange} min={fitType === 'Driver' ? 60 : 50} max={fitType === 'Driver' ? 140 : 110} unit="mph" tooltip={TOOLTIPS.club_speed} required />
                  <div>
                    <SliderInput label={`Ball Speed ${!ballSpeedOverride ? '(auto)' : ''}`} val={manualData.ball_speed} set={(v) => setManualData({ ...manualData, ball_speed: v })} min={fitType === 'Driver' ? 80 : 70} max={fitType === 'Driver' ? 220 : 170} unit="mph" tooltip={TOOLTIPS.ball_speed} required disabled={!ballSpeedOverride} />
                    <label className="flex items-center gap-2 mt-2 text-xs text-text-secondary cursor-pointer">
                      <input type="checkbox" checked={ballSpeedOverride} onChange={(e) => setBallSpeedOverride(e.target.checked)} className="rounded bg-surface border-border" />
                      Override (current: {(manualData.ball_speed / manualData.club_speed).toFixed(2)} smash)
                    </label>
                  </div>
                  <SliderInput label="Spin (RPM)" val={manualData.spin_rate} set={(v) => setManualData({ ...manualData, spin_rate: v })} min={1000} max={10000} unit="rpm" tooltip={TOOLTIPS.spin_rate} required />
                  <SliderInput label="Launch Angle" val={manualData.launch_angle} set={(v) => setManualData({ ...manualData, launch_angle: v })} min={5} max={30} unit="deg" tooltip={TOOLTIPS.launch_angle} required />
                </div>

                {/* Golf Ball Selection */}
              </div>


              {/* Additional Metrics Section */}
              <div className="space-y-4 pt-4 border-t border-border">
                <div className="text-xs font-bold text-text-muted uppercase tracking-wider flex items-center justify-between">
                  Additional Metrics
                  <button onClick={() => setShowAdvanced(!showAdvanced)} className="text-xs text-primary hover:text-primary-light transition-colors">
                    {showAdvanced ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showAdvanced && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 sm:gap-y-3 animate-in fade-in">
                    {/* Player Profile */}
                    <div className="space-y-2 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                      <div className="text-sm font-semibold text-zinc-300 mb-2">Player Profile</div>
                      <AdvSliderInput label="Handicap" val={manualData.handicap} set={v => setManualData({...manualData, handicap: v})} min={0} max={36} unit="" tooltip={TOOLTIPS.handicap} />
                      <AdvSliderInput label="Wrist-to-Floor" val={manualData.wrist_to_floor} set={v => setManualData({...manualData, wrist_to_floor: v})} min={28} max={42} unit="in" tooltip={TOOLTIPS.wrist_to_floor} />
                    </div>

                    {/* Shot Shape */}
                    <div className="space-y-2 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                      <div className="text-sm font-semibold text-zinc-300 mb-2">Shot Shape</div>
                      <AdvSliderInput label={spinInputMode === 'axis' ? 'Spin Axis' : 'Side Spin'} val={spinInputMode === 'axis' ? manualData.spin_axis : manualData.side_spin} set={v => setManualData({...manualData, [spinInputMode === 'axis' ? 'spin_axis' : 'side_spin']: v})} min={spinInputMode === 'axis' ? -20 : -2000} max={spinInputMode === 'axis' ? 20 : 2000} unit={spinInputMode === 'axis' ? 'deg' : 'rpm'} tooltip={spinInputMode === 'axis' ? TOOLTIPS.spin_axis : TOOLTIPS.side_spin} />
                      <div className="flex gap-1 ml-2">
                        <button onClick={() => setSpinInputMode('axis')} className={`text-[10px] px-1.5 py-0.5 rounded ${spinInputMode === 'axis' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-500'}`}>Axis°</button>
                        <button onClick={() => setSpinInputMode('sidespin')} className={`text-[10px] px-1.5 py-0.5 rounded ${spinInputMode === 'sidespin' ? 'bg-blue-600 text-white' : 'bg-zinc-800 text-zinc-500'}`}>RPM</button>
                      </div>
                    </div>

                    {/* Club Delivery */}
                    <div className="space-y-2 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                      <div className="text-sm font-semibold text-zinc-300 mb-2">Club Delivery</div>
                      <AdvSliderInput label="Attack Angle" val={manualData.attack_angle} set={v => setManualData({...manualData, attack_angle: v})} min={-5} max={5} unit="deg" step={0.1} tooltip={TOOLTIPS.attack_angle} />
                      <AdvSliderInput label="Club Path" val={manualData.club_path} set={v => setManualData({...manualData, club_path: v})} min={-10} max={10} unit="deg" step={0.1} />
                      <AdvSliderInput label="Face Angle" val={manualData.face_angle} set={v => setManualData({...manualData, face_angle: v})} min={-5} max={5} unit="deg" step={0.1} />
                      <AdvSliderInput label="Face to Path" val={manualData.face_to_path} set={v => setManualData({...manualData, face_to_path: v})} min={-5} max={5} unit="deg" step={0.1} />
                      <AdvSliderInput label="Dynamic Loft" val={manualData.dynamic_loft} set={v => setManualData({...manualData, dynamic_loft: v})} min={8} max={30} unit="deg" step={0.1} />
                    </div>

                    {/* Ball Flight */}
                    <div className="space-y-2 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                      <div className="text-sm font-semibold text-zinc-300 mb-2">Ball Flight</div>
                      <AdvSliderInput label="Max Height" val={manualData.max_height} set={v => setManualData({...manualData, max_height: v})} min={10} max={50} unit="yds" />
                      <AdvSliderInput label="Landing Angle" val={manualData.descent_angle} set={v => setManualData({...manualData, descent_angle: v})} min={25} max={55} unit="deg" />
                      <AdvSliderInput label="Hang Time" val={manualData.flight_time} set={v => setManualData({...manualData, flight_time: v})} min={3} max={9} unit="s" step={0.1} />
                      <AdvSliderInput label="Total Distance" val={manualData.total_distance} set={v => setManualData({...manualData, total_distance: v})} min={150} max={350} unit="yds" />
                      <AdvSliderInput label="Side Carry/Offline" val={manualData.offline_distance} set={v => setManualData({...manualData, offline_distance: v})} min={-30} max={30} unit="yds" />
                    </div>

                    {/* Spin & Impact */}
                    <div className="space-y-2 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                      <div className="text-sm font-semibold text-zinc-300 mb-2">Spin & Impact</div>
                      <AdvSliderInput label="Spin Loft" val={manualData.spin_loft} set={v => setManualData({...manualData, spin_loft: v})} min={10} max={30} unit="deg" step={0.1} />
                      <AdvSliderInput label="Smash Factor" val={manualData.smash_factor} set={v => setManualData({...manualData, smash_factor: v})} min={1.2} max={1.55} unit="" step={0.01} />
                      <AdvSliderInput label="Low Point" val={manualData.low_point} set={v => setManualData({...manualData, low_point: v})} min={-4} max={4} unit="in" step={0.1} />
                    </div>

                    {/* Playing Conditions */}
                    <div className="col-span-2 space-y-2 p-3 bg-zinc-900/50 rounded-lg border border-zinc-800/50">
                      <div className="text-sm font-semibold text-zinc-300 mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Cloud size={14}/> Playing Conditions
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-xs ${usePlayingConditions ? 'text-status-success' : 'text-text-muted'}`}>
                            {usePlayingConditions ? 'ON' : 'OFF'}
                          </span>
                          <button
                            onClick={() => setUsePlayingConditions(!usePlayingConditions)}
                            className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                              usePlayingConditions ? 'bg-secondary justify-end' : 'bg-border justify-start'
                            }`}
                          >
                            <span className="w-5 h-5 bg-white rounded-full" />
                          </button>
                        </div>
                      </div>
                      {weather ? (
                        <div className="flex items-center gap-2 bg-status-success/10 border border-status-success/30 text-status-success px-3 py-2 rounded-lg text-xs">
                          <MapPin size={14} className="flex-shrink-0"/>
                          <span>Auto-detected: <strong>{weather.summary}</strong> &mdash; carry projections adjusted</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-surface border border-border text-text-secondary px-3 py-2 rounded-lg text-xs">
                          <Cloud size={14} className="flex-shrink-0"/>
                          <span>Allow location access to auto-fill weather, or set manually below</span>
                        </div>
                      )}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                        <AdvSliderInput label="Altitude" val={manualData.altitude_ft} set={(v) => setManualData({ ...manualData, altitude_ft: v })} min={0} max={10000} unit="ft" tooltip="Elevation above sea level. Higher altitude = thinner air = more distance." />
                        <AdvSliderInput label="Temperature" val={manualData.temp_f} set={(v) => setManualData({ ...manualData, temp_f: v })} min={30} max={110} unit="°F" tooltip="Air temperature. Warmer air = less density = more distance." />
                        <AdvSliderInput label="Humidity" val={manualData.humidity_pct} set={(v) => setManualData({ ...manualData, humidity_pct: v })} min={0} max={100} unit="%" tooltip="Relative humidity. Humid air is less dense than dry air." />
                        <AdvSliderInput label="Wind Speed" val={manualData.wind_speed_mph} set={(v) => setManualData({ ...manualData, wind_speed_mph: v })} min={0} max={30} unit="mph" tooltip="Wind speed." />
                        <AdvSliderInput label="Wind Direction" val={manualData.wind_direction_deg} set={(v) => setManualData({ ...manualData, wind_direction_deg: v })} min={0} max={360} unit="°" tooltip="Wind direction in degrees (0 = from behind, 180 = headwind)." />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Gear Input Toggle */}
              <div className="pt-4 border-t border-border">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Settings className="text-primary" size={20} />
                    <div>
                      <div className="font-medium text-white">Include Current Gear (Optional)</div>
                      <div className="text-xs text-zinc-500">Get personalized optimization suggestions</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowGearInput(!showGearInput)}
                    className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                      showGearInput ? 'bg-primary justify-end' : 'bg-border justify-start'
                    }`}
                  >
                    <span className="w-5 h-5 bg-white rounded-full" />
                  </button>
                </div>

                {showGearInput && (
                  <CurrentGearInput
                    ref={gearInputRef}
                    category={fitType === 'Driver' ? clubType : 'Iron'}
                  />
                )}
              </div>

              <DSButton
                id="run-analysis-btn"
                variant="primary"
                size="lg"
                fullWidth
                isLoading={loading}
                onClick={handleManual}
              >
                Calculate Fit
              </DSButton>
            </Card>
          ) : mode === 'csv' ? (
            <div className="space-y-4">
              <Card padding="p-4">
                <div className="text-xs text-zinc-500 uppercase mb-2">Select Club Being Fitted</div>
                <div className="flex items-center gap-4">
                  {fitType === 'Driver' ? (
                    <ButtonGroup>{['Driver', 'Fairway', 'Hybrid'].map(t => <ToggleButton key={t} onClick={() => setClubType(t)} active={clubType === t} activeColor="bg-primary">{t}</ToggleButton>)}</ButtonGroup>
                  ) : (
                    <ButtonGroup>{['4i', '5i', '6i', '7i', '8i', '9i', 'PW'].map(t => <ToggleButton key={t} onClick={() => setIronType(t)} active={ironType === t} activeColor="bg-success">{t}</ToggleButton>)}</ButtonGroup>
                  )}
                </div>
              </Card>

              {/* NEW: Isolated CSV Uploader Component */}
              <CSVUploader
                category={fitType === 'Driver' ? clubType : 'Iron'}
                clubType={fitType === 'Driver' ? clubType : ironType}
                dexterity={dexterity}
                onUploadComplete={(data) => {
                  console.log('CSV Upload complete:', data);
                  setUploadedData({ ...data, selectedClub: fitType === 'Driver' ? clubType : ironType });
                  setUploadStatus('CSV uploaded successfully');
                  // NO SHOT CURATION - skip analysis
                }}
              />

              {/* NO SHOT CURATION - Skip directly to gear input */}

              {/* Gear Input Toggle - Shows after successful upload */}
              {uploadedData && !result && (
              <Card padding="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Settings className="text-primary" size={20} />
                    <div>
                      <div className="font-medium text-white">Include Current Gear (Optional)</div>
                      <div className="text-xs text-zinc-500">Get personalized optimization suggestions</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowGearInput(!showGearInput)}
                    className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                      showGearInput ? 'bg-primary justify-end' : 'bg-border justify-start'
                    }`}
                  >
                    <span className="w-5 h-5 bg-white rounded-full" />
                  </button>
                </div>

                {showGearInput && (
                  <CurrentGearInput
                    ref={gearInputRef}
                    category={fitType === 'Driver' ? clubType : 'Iron'}
                  />
                )}

                <div className="mt-4">
                  <DSButton
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={() => {
                      const gear = showGearInput && gearInputRef.current ? gearInputRef.current.getGearData() : null;
                      setCurrentGear(gear);
                      proceedWithUploadedData(gear);
                    }}
                  >
                    Calculate Fit
                  </DSButton>
                </div>
              </Card>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <Card padding="p-4">
                <div className="text-xs text-zinc-500 uppercase mb-2">Select Club Being Fitted</div>
                <div className="flex items-center gap-4">
                  {fitType === 'Driver' ? (
                    <ButtonGroup>{['Driver', 'Fairway', 'Hybrid'].map(t => <ToggleButton key={t} onClick={() => setClubType(t)} active={clubType === t} activeColor="bg-primary">{t}</ToggleButton>)}</ButtonGroup>
                  ) : (
                    <ButtonGroup>{['4i', '5i', '6i', '7i', '8i', '9i', 'PW'].map(t => <ToggleButton key={t} onClick={() => setIronType(t)} active={ironType === t} activeColor="bg-success">{t}</ToggleButton>)}</ButtonGroup>
                  )}
                </div>
              </Card>

              {/* NEW: Isolated OCR Uploader Component */}
              <OCRUploader
                category={fitType === 'Driver' ? clubType : 'Iron'}
                clubType={fitType === 'Driver' ? clubType : ironType}
                dexterity={dexterity}
                onUploadComplete={(data) => {
                  setUploadedData({ ...data, selectedClub: fitType === 'Driver' ? clubType : ironType });
                  setUploadStatus('Image uploaded successfully');
                  // NO SHOT CURATION - skip analysis
                }}
              />

              {/* NO SHOT CURATION - Skip directly to gear input */}

              {/* Gear Input Toggle - Shows after successful upload */}
              {uploadedData && !result && (
              <Card padding="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <Settings className="text-primary" size={20} />
                    <div>
                      <div className="font-medium text-white">Include Current Gear (Optional)</div>
                      <div className="text-xs text-zinc-500">Get personalized optimization suggestions</div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowGearInput(!showGearInput)}
                    className={`w-12 h-6 flex items-center rounded-full p-1 transition-colors ${
                      showGearInput ? 'bg-primary justify-end' : 'bg-border justify-start'
                    }`}
                  >
                    <span className="w-5 h-5 bg-white rounded-full" />
                  </button>
                </div>

                {showGearInput && (
                  <CurrentGearInput
                    ref={gearInputRef}
                    category={fitType === 'Driver' ? clubType : 'Iron'}
                  />
                )}

                <div className="mt-4">
                  <DSButton
                    variant="primary"
                    size="lg"
                    fullWidth
                    onClick={() => {
                      const gear = showGearInput && gearInputRef.current ? gearInputRef.current.getGearData() : null;
                      setCurrentGear(gear);
                      proceedWithUploadedData(gear);
                    }}
                  >
                    Calculate Fit
                  </DSButton>
                </div>
              </Card>
              )}
            </div>
          )}
        </>
      ) : (
        <ErrorBoundary>
        <div className="space-y-6 animate-in fade-in">
          <div className="flex justify-between items-center">
            <button onClick={() => {
              setResult(null);
              setCurrentGear(null);
              setOptimizationData(null);
              setUploadedData(null);
              setUploadStatus(null);
              setError(null);
              // Clear file input to allow re-uploading
              if (fileInputRef.current) fileInputRef.current.value = '';
            }} className="text-zinc-500 hover:text-white text-sm flex items-center gap-1">← New Calculation</button>
            <div className="flex items-center gap-3">
              {result.shot_count > 0 && <span className="text-xs text-zinc-500 bg-zinc-800 px-2 py-1 rounded">{result.shot_count} shots</span>}
            </div>
          </div>

          {/* Always show matrix on results page */}
          <>

            {/* Show session metrics for CSV/OCR uploads */}
            {(result.best_shot || result.aggregated) && (
            <div className="grid md:grid-cols-2 gap-4">
              {/* Best Shot */}
              {result.best_shot && (
                <div className="bg-zinc-900/50 p-4 rounded-lg border border-zinc-800">
                  <div className="text-xs text-zinc-500 uppercase mb-2">Best Shot (Highest Carry)</div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-zinc-500">Ball Speed:</span> <span className="text-white font-mono">{result.best_shot.ball_speed || '-'} mph</span></div>
                    <div><span className="text-zinc-500">Carry:</span> <span className="text-emerald-400 font-mono font-bold">{result.best_shot.carry_distance || '-'} yds</span></div>
                    <div><span className="text-zinc-500">Launch:</span> <span className="text-white font-mono">{result.best_shot.launch_angle || '-'}°</span></div>
                    <div><span className="text-zinc-500">Spin:</span> <span className="text-white font-mono">{result.best_shot.spin_rate || '-'} rpm</span></div>
                  </div>
                </div>
              )}
              {/* Baseline - What fitter uses for calculations */}
              {result.aggregated && (
                <div className="bg-blue-900/20 p-4 rounded-lg border border-blue-800/50">
                  <div className="text-xs text-blue-400 uppercase mb-2 flex items-center gap-2">
                    <Activity size={12} /> Baseline (Session Average)
                    <span className="text-blue-500/60 text-[10px] normal-case">· Used for matrix calculations</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div><span className="text-zinc-500">Ball Speed:</span> <span className="text-white font-mono">{Math.round(result.aggregated.ball_speed) || '-'} mph</span></div>
                    <div><span className="text-zinc-500">Carry:</span> <span className="text-blue-400 font-mono font-bold">{Math.round(result.aggregated.carry_distance) || '-'} yds</span></div>
                    <div><span className="text-zinc-500">Launch:</span> <span className="text-white font-mono">{result.aggregated.launch_angle?.toFixed(1) || '-'}°</span></div>
                    <div><span className="text-zinc-500">Spin:</span> <span className="text-white font-mono">{Math.round(result.aggregated.spin_rate) || '-'} rpm</span></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Show Optimization Suggestion if available */}
          {optimizationData && currentGear && (
            <OptimizationSuggestionCard
              clubModel={currentGear.clubModel}
              currentLoft={currentGear.loftDegrees}
              optimizationData={optimizationData}
              onProceed={() => {}} // No-op since we're already showing matrix below
            />
          )}

          {/* Matrix View - 3x3 head/shaft grid */}
          {result?.matrix ? (
            <FitterMatrix result={result} onSave={saveSession} saving={saving} weather={weather} />
          ) : (
            <div className="text-zinc-500 text-center p-8">No results available</div>
          )}

          {/* Shots Section with Dispersion Chart */}
          {result.shots && result.shots.length > 0 && (
            <Card padding="p-0" className="overflow-hidden">
              <div className="bg-page px-4 py-3 text-sm font-bold text-zinc-300 flex items-center gap-2"><FileText size={14}/> Shot Data ({result.shots.length} shots)</div>

              {/* Dispersion Chart */}
              <div className="p-4 border-b border-subtle">
                <div className="text-xs text-zinc-500 mb-2 text-center">Shot Dispersion</div>
                <DispersionChart shots={result.shots} />
              </div>

              {/* Shots Table */}
              <div className="max-h-48 overflow-y-auto overflow-x-auto">
                <table className="w-full text-xs min-w-[500px]">
                  <thead className="bg-page text-zinc-500 sticky top-0"><tr><th className="p-2 text-left">#</th><th className="p-2 text-right">Ball</th><th className="p-2 text-right">Spin</th><th className="p-2 text-right">Launch</th><th className="p-2 text-right">Carry</th><th className="p-2 text-right">Path</th><th className="p-2 text-right">Face</th><th className="p-2 text-right">Offline</th></tr></thead>
                  <tbody>
                    {result.shots.map((s, i) => (
                      <tr key={i} className={`border-t border-subtle ${s.carry_distance === result.best_shot?.carry_distance ? 'bg-success-muted' : ''}`}>
                        <td className="p-2 text-zinc-500">{s.shot_number}</td>
                        <td className="p-2 text-right text-zinc-300">{s.ball_speed || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{s.spin_rate || '-'}</td>
                        <td className="p-2 text-right text-zinc-300">{s.launch_angle || '-'}</td>
                        <td className="p-2 text-right font-mono text-success">{s.carry_distance || '-'}</td>
                        <td className="p-2 text-right text-zinc-400">{s.club_path != null ? `${s.club_path > 0 ? '+' : ''}${s.club_path}°` : (s.spin_axis != null ? `${s.spin_axis > 0 ? '+' : ''}${s.spin_axis}°` : '-')}</td>
                        <td className="p-2 text-right text-zinc-400">{s.face_angle != null ? `${s.face_angle > 0 ? '+' : ''}${s.face_angle}°` : '-'}</td>
                        <td className="p-2 text-right text-zinc-400">{s.offline_distance != null ? `${s.offline_distance > 0 ? 'R' : 'L'}${Math.abs(s.offline_distance).toFixed(0)}` : (s.side_spin != null ? `${s.side_spin > 0 ? 'R' : 'L'}${Math.abs(s.side_spin/100).toFixed(0)}` : '-')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
            )}
          </>
        </div>
        </ErrorBoundary>
      )}
      {error && <div className="bg-red-900/20 border border-red-900/50 text-red-200 p-4 rounded-lg flex items-center gap-3"><AlertCircle className="w-5 h-5" />{error}</div>}
    </div>
    </ErrorBoundary>
  );
}
